import { useState } from "react";

import {
  getDatadogClusterDashboardUrl,
  getDatadogClusterLogsUrl,
  getDatadogClusterTracesUrl,
  type DatadogScopedCluster,
} from "~/utils";

import { Icon } from "./icon";

interface DatadogLinksProps {
  clusters: DatadogScopedCluster | DatadogScopedCluster[];
  /** "dropdown" for icon button with hover menu, "inline" for text links */
  variant?: "dropdown" | "inline";
  /** Button style: "solid" matches action buttons, "subtle" for lists */
  buttonStyle?: "solid" | "subtle";
  /** "sm" fits inline rows like a wave header. */
  size?: "sm" | "md";
  /** Drop the "View in Datadog:" prefix when the context already says so. */
  withPrefix?: boolean;
}

export function DatadogLinks({
  clusters,
  variant = "dropdown",
  buttonStyle = "solid",
  size = "md",
  withPrefix = true,
}: DatadogLinksProps) {
  const [isOpen, setIsOpen] = useState(false);

  const metricsUrl = getDatadogClusterDashboardUrl(clusters);
  const logsUrl = getDatadogClusterLogsUrl(clusters);
  const tracesUrl = getDatadogClusterTracesUrl(clusters);

  if (!metricsUrl && !logsUrl && !tracesUrl) return null;

  const links = [
    { url: metricsUrl, label: "Metrics", icon: "Activity" as const },
    { url: logsUrl, label: "Logs", icon: "ScrollText" as const },
    { url: tracesUrl, label: "Traces", icon: "Waypoints" as const },
  ].filter((link) => link.url);

  if (variant === "inline") {
    return (
      <div className="flex items-center gap-1 text-sm">
        {withPrefix ? (
          <span className="text-gray-600">View in Datadog:</span>
        ) : null}
        {links.map(({ url, label }, index) => (
          <span key={label} className="flex items-center">
            <a
              href={url!}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-800 hover:underline"
            >
              {label}
            </a>
            {index < links.length - 1 ? (
              <span className="mx-1 text-gray-400">·</span>
            ) : null}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-center rounded-sm border transition-all ${
          size === "sm" ? "h-5 w-5" : "h-8 w-8"
        } ${
          buttonStyle === "solid"
            ? "border-purple-950 bg-purple-300 text-purple-950 shadow-btn hover:bg-purple-400 hover:shadow-btn-hover active:bg-purple-400 active:shadow-btn-active"
            : "border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100"
        }`}
        title="Open Datadog"
      >
        <Icon name="Dog" size={size === "sm" ? 12 : 16} />
      </button>

      {isOpen ? (
        // The small button sits at a row's right edge, so its menu hangs left.
        <div
          className={`absolute top-full z-50 pt-1 ${size === "sm" ? "right-0" : "left-0"}`}
        >
          <div className="min-w-[140px] rounded-sm border border-gray-200 bg-white py-1 shadow-lg">
            {links.map(({ url, label, icon }) => (
              <a
                key={label}
                href={url!}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Icon name={icon} size={14} className="text-gray-500" />
                {label}
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
